import { inject, Injectable } from '@angular/core';

import { Store } from '@ngxs/store';
import { FilterListItemModel } from 'tsui';
import { FieldDef } from '@core/models';
import { FieldDefsState } from '@state/fields/state';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums/feature-flag';

@Injectable({
  providedIn: 'root',
})
export class TsFilterUtilsService {
  protected readonly store = inject(Store);
  readonly hasNestedFiltersOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));

  getFilterList(fieldDefs: FieldDef[]): FilterListItemModel[] {
    const filteredFieldDefs = fieldDefs.filter((fieldDef) => {
      if (this.hasNestedFiltersOn) {
        // When nestedFilters is enabled, exclude fields with exclude_in_nested_filter === true.
        return fieldDef.meta?.filtering?.exclude_in_nested_filter !== true;
      }

      // When nestedFilters is disabled, include all fields
      // Check if field has filtering operators/functions or sorting capability.
      return (
        (fieldDef.meta?.filtering?.operators && fieldDef.meta.filtering.operators.length > 0) ||
        (fieldDef.meta?.filtering?.functions && fieldDef.meta.filtering.functions.length > 0) ||
        !!fieldDef.meta?.sorting ||
        fieldDef.meta?.filtering !== undefined
      );
    });

    const processedFieldDefs = filteredFieldDefs.map(
      ({ label, name, type, meta, _parentFieldLabel, _parentFieldMeta, _parentFieldName }) => {
        const fieldOptions = this.formatFieldOptions(meta);

        return {
          label,
          meta: {
            filtering: {
              operators: meta?.filtering?.operators || [],
              functions: meta?.filtering?.functions || [],
              exclude_in_nested_filter: meta?.filtering?.exclude_in_nested_filter || false,
            },
            options: fieldOptions,
            multi_select: meta?.multi_select || false,
            record_type: meta?.record_type || null,
            group: meta?.group,
            scale: meta?.scale,
            sorting: meta?.sorting,
          },
          name,
          type,
          _parentFieldLabel,
          _parentFieldMeta,
          _parentFieldName,
        } as FilterListItemModel;
      },
    );

    return processedFieldDefs;
  }

  private formatFieldOptions(meta: any): string[] | { label: string; value: string }[] {
    const dropdownOptions = this.store.selectSnapshot(FieldDefsState.selectDropdownOptionsBy(meta?.option_name));

    if (dropdownOptions) {
      return dropdownOptions.map((option) => ({ label: option.name, value: option.id }));
    }

    return meta?.options || [];
  }
}
